
import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaBook,
  FaGraduationCap,
  FaCommentDots,
} from "react-icons/fa";

import { useLocation } from "react-router-dom";
import brochureLinks from "../data/brochures";
import TurnstileWidget from "./TurnstileWidget";

const RegistrationForm = () => {
  const location = useLocation();
  const selectedCourse = location.state?.selectedCourse || "";

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    course: selectedCourse,
    qualification: "",
    message: "",
  });

  const [status, setStatus] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  // Cloudflare Turnstile security state
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
  }, []);

  // Update course if selected from the previous page
  useEffect(() => {
    if (selectedCourse) {
      setFormData((prev) => ({
        ...prev,
        course: selectedCourse,
      }));
    }
  }, [selectedCourse]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sending) return;

    // Require security verification before submitting
    if (!turnstileToken) {
      setSubmitted(false);
      setStatus(
        "⚠️ Please complete the security verification."
      );
      return;
    }

    setSending(true);
    setSubmitted(false);
    setStatus("Submitting...");

    try {
      const registrationMessage = `
Course / Service Interested:
${formData.course.trim()}

Qualification / Business Domain:
${formData.qualification.trim()}

Additional Message:
${formData.message.trim() || "Not provided"}
      `.trim();

      // Submit registration to the existing protected API
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          userType: "Course Registration",
          message: registrationMessage,
          turnstileToken,
        }),
      });

      let result = {};

      try {
        result = await response.json();
      } catch {
        result = {};
      }

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            "Unable to submit your registration."
        );
      }

      // Normalize course names for brochure matching
      const nameMap = {
        "full stack development": "Web Development",
        python: "Python Full Stack",
        java: "Java Full Stack",
        "html, css, js, react": "Web Development",
        "php, wordpress": "Web Development",
        "data analytics": "Data Analytics",
        "data science": "Data Science",
        "digital marketing": "Digital Marketing",
        "power bi": "Data Analytics",
        "c / c++ programming": "Common",
        "networking & infrastructure management": "Common",
        "hr training & office automation": "Common",
        "testing & automation": "Common",
        "ai & ml": "Common",
        sap: "Common",
        "microsoft dynamics": "Common",
        autocad: "Common",
        "embedded systems & vlsi": "Common",
      };

      const normalizedCourse = formData.course
        .trim()
        .toLowerCase();

      const mappedCourse =
        nameMap[normalizedCourse] || formData.course.trim();

      // Find matching brochure
      const brochureKey =
        Object.keys(brochureLinks).find(
          (key) =>
            key.toLowerCase() === mappedCourse.toLowerCase()
        ) || "Common";

      const brochureLink = brochureLinks[brochureKey];

      if (brochureLink) {
        window.open(brochureLink, "_blank");
      } else {
        console.warn(
          "No brochure found for:",
          formData.course
        );
      }

      // Registration success
      setStatus("✅ Registration submitted successfully!");
      setSubmitted(true);

      // Reset form while preserving the preselected course
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        course: selectedCourse,
        qualification: "",
        message: "",
      });

      window.setTimeout(() => {
        setSubmitted(false);
        setStatus("");
      }, 5000);
    } catch (error) {
      console.error("Registration form error:", error);

      setSubmitted(false);

      setStatus(
        error.message ||
          "⚠️ Failed to submit registration. Please try again."
      );
    } finally {
      setSending(false);

      // Clear the used token and create a fresh widget
      setTurnstileToken("");
      setTurnstileKey((previous) => previous + 1);
    }
  };

  const inputFields = [
    {
      name: "fullName",
      type: "text",
      placeholder: "Full Name / Company Name",
      icon: <FaUser />,
      autoComplete: "name",
    },
    {
      name: "email",
      type: "email",
      placeholder: "Email Address",
      icon: <FaEnvelope />,
      autoComplete: "email",
    },
    {
      name: "phone",
      type: "tel",
      placeholder: "Phone Number",
      icon: <FaPhone />,
      autoComplete: "tel",
    },
    {
      name: "course",
      type: "text",
      placeholder: "Course / Service Interested",
      icon: <FaBook />,
      autoComplete: "off",
    },
    {
      name: "qualification",
      type: "text",
      placeholder: "Qualification / Business Domain",
      icon: <FaGraduationCap />,
      autoComplete: "organization-title",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="
        w-full
        max-w-lg
        mx-auto
        rounded-3xl
        border
        border-blue-200/30
        bg-white/90
        p-10
        shadow-2xl
        backdrop-blur-xl
        transition-all
        duration-500
        hover:shadow-blue-400/30
        dark:border-gray-700
        dark:bg-gray-900/90
      "
    >
      <h2
        className="
          mb-8
          text-center
          text-3xl
          font-bold
          bg-gradient-to-r
          from-blue-700
          via-indigo-600
          to-purple-600
          bg-clip-text
          text-transparent
          md:text-4xl
        "
      >
        Register Now
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        {inputFields.map((field, index) => (
          <motion.div
            key={field.name}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{
              duration: 0.5,
              delay: 0.1 * index,
            }}
            className="relative"
          >
            <span
              className="
                absolute
                left-3
                top-3
                text-lg
                text-blue-600
                dark:text-blue-300
              "
            >
              {field.icon}
            </span>

            <input
              type={field.type}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              placeholder={field.placeholder}
              required
              disabled={sending}
              readOnly={
                field.name === "course" &&
                !!selectedCourse
              }
              autoComplete={field.autoComplete}
              className="
                w-full
                rounded-xl
                border-2
                border-gray-300
                bg-transparent
                py-3
                pl-10
                pr-3
                text-gray-800
                outline-none
                transition-all
                duration-300
                focus:border-blue-500
                focus:ring-2
                focus:ring-blue-300
                disabled:cursor-not-allowed
                disabled:opacity-70
                dark:border-gray-600
                dark:text-gray-100
              "
            />
          </motion.div>
        ))}

        {/* Optional message */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{
            duration: 0.5,
            delay: 0.6,
          }}
          className="relative"
        >
          <span
            className="
              absolute
              left-3
              top-3
              text-lg
              text-blue-600
              dark:text-blue-300
            "
          >
            <FaCommentDots />
          </span>

          <textarea
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder="Message (Optional)"
            disabled={sending}
            className="
              w-full
              resize-none
              rounded-xl
              border-2
              border-gray-300
              bg-transparent
              py-3
              pl-10
              pr-3
              text-gray-800
              outline-none
              transition-all
              duration-300
              focus:border-blue-500
              focus:ring-2
              focus:ring-blue-300
              disabled:cursor-not-allowed
              disabled:opacity-70
              dark:border-gray-600
              dark:text-gray-100
            "
          />
        </motion.div>

        {/* Cloudflare Turnstile security verification */}
        <div className="flex justify-center overflow-x-auto">
          <TurnstileWidget
            key={turnstileKey}
            onVerify={handleTurnstileVerify}
          />
        </div>

        {/* Submit */}
        <motion.button
          whileHover={
            !sending
              ? {
                  scale: 1.05,
                }
              : undefined
          }
          whileTap={!sending ? { scale: 0.97 } : undefined}
          type="submit"
          disabled={sending || submitted}
          className="
            w-full
            rounded-xl
            bg-gradient-to-r
            from-blue-600
            via-indigo-600
            to-purple-600
            py-3
            font-semibold
            text-white
            shadow-lg
            transition-all
            duration-300
            hover:shadow-blue-400/30
            disabled:cursor-not-allowed
            disabled:opacity-70
          "
        >
          {sending
            ? "Submitting..."
            : submitted
              ? "Submitted ✅"
              : "Submit & Download Brochure"}
        </motion.button>
      </form>

      {status && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          role="status"
          aria-live="polite"
          className={`mt-5 text-center font-semibold ${
            status.includes("✅")
              ? "text-green-600 dark:text-green-400"
              : status.includes("⚠️")
                ? "text-yellow-600 dark:text-yellow-400"
                : "text-blue-600 dark:text-blue-400"
          }`}
        >
          {status}
        </motion.p>
      )}
    </motion.div>
  );
};

export default RegistrationForm;
